import { useEffect } from 'react';
import { Music, Trash2, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { useScoreStore } from '@/stores';
import type { Score, ScoreStatus } from '@/types';

interface ScoreLibraryProps {
  onScoreSelect: (scoreId: string) => void;
  onUploadClick: () => void;
}

export function ScoreLibrary({ onScoreSelect, onUploadClick }: ScoreLibraryProps) {
  const scores = useScoreStore((state) => state.scores);
  const deleteScore = useScoreStore((state) => state.deleteScore);
  const fetchScores = useScoreStore((state) => state.fetchScores);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  if (scores.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No scores yet</h2>
          <p className="text-gray-400 mb-4">Upload a PDF, MusicXML, or MIDI file to get started</p>
          <button
            onClick={onUploadClick}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
          >
            Upload Your First Score
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">Your Scores</h2>

        <div className="grid gap-4">
          {scores.map((score) => (
            <ScoreCard
              key={score.id}
              score={score}
              onSelect={() => onScoreSelect(score.id)}
              onDelete={() => deleteScore(score.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface ScoreCardProps {
  score: Score;
  onSelect: () => void;
  onDelete: () => void;
}

function ScoreCard({ score, onSelect, onDelete }: ScoreCardProps) {
  const statusConfig: Record<ScoreStatus, { icon: typeof Clock; color: string; text: string }> = {
    pending: { icon: Clock, color: 'text-gray-400', text: 'Pending...' },
    uploading: { icon: Clock, color: 'text-yellow-400', text: 'Uploading...' },
    processing: { icon: Clock, color: 'text-blue-400', text: 'Processing...' },
    ready: { icon: CheckCircle, color: 'text-green-400', text: 'Ready' },
    error: { icon: AlertCircle, color: 'text-red-400', text: 'Error' },
    needs_correction: { icon: AlertCircle, color: 'text-yellow-400', text: 'Needs Review' },
  };

  // Defensive fallback: if backend ships an unknown status, render as error
  // instead of crashing the whole library view (TypeError: undefined.icon).
  const status = statusConfig[score.status] ?? statusConfig.error;
  const StatusIcon = status.icon;

  const isClickable = score.status === 'ready' || score.status === 'needs_correction';

  return (
    <div
      className={`bg-gray-800 rounded-lg p-4 border border-gray-700 ${
        isClickable ? 'cursor-pointer hover:bg-gray-750 hover:border-gray-600' : ''
      } transition-colors`}
      onClick={isClickable ? onSelect : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
            <Music className="w-6 h-6 text-gray-400" />
          </div>

          {/* Info */}
          <div>
            <h3 className="font-semibold text-lg">{score.title}</h3>
            {score.composer && <p className="text-gray-400 text-sm">{score.composer}</p>}
            <div className="flex items-center gap-4 mt-2 text-sm">
              {/* Status */}
              <span className={`flex items-center gap-1 ${status.color}`}>
                <StatusIcon className="w-4 h-4" />
                {status.text}
              </span>

              {/* Source type */}
              <span className="text-gray-500">{score.sourceType.toUpperCase()}</span>

              {/* PDF quality indicator */}
              {score.pdfClassification && (
                <span className="text-gray-500">
                  {score.pdfClassification.type === 'vector' ? 'Digital' : 'Scanned'}
                  {' · '}~{Math.round(score.pdfClassification.estimatedAccuracy * 100)}% accuracy
                </span>
              )}
            </div>

            {/* Error message */}
            {score.status === 'error' && score.errorMessage && (
              <p className="text-red-400 text-sm mt-2">{score.errorMessage}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
